import styled from '@emotion/styled';
import { useContext } from 'react';
import { buyerUserContext } from 'application/contexts/useBuyerUser';
import { IMatch } from 'domain/entities/Match/Match';
import NoImage from 'ui/images/no_image_case.png';
import LinkIcon from 'ui/images/link.svg';
import { mqUser } from 'utils/responsive';

const Wrapper = styled.div`
    padding: 10px 20px;
    border-radius: 20px;
    margin: 10px auto;
    text-align: center;
    box-shadow: 0 0 20px rgba(0, 0, 0, 0.1);
    min-width: 400px;

    ${mqUser('SP')} {
        min-width: 90svw;
        width: 100%;
    }

    img {
        width: 170px;
        margin: 0 auto;
    }
`;

const Title = styled.div`
    font-size: 18px;
    font-weight: 600;
    margin-bottom: 20px;
    line-height: 1.5;
    background: linear-gradient(45deg, #daa520, #b8860b);
    color: #fff;
    padding: 7px;
`;

const Card = styled.div`
    background: linear-gradient(to right, #fff8e1, #ffeb99);
    padding: 15px 15px 20px 15px;
    border-radius: 10px;
    box-shadow: 0px 2px 10px rgba(0, 0, 0, 0.1);
    display: flex;
    flex-direction: column;
    gap: 20px;
    cursor: pointer;
`;

const DescContainer = styled.div`
    display: flex;
    gap: 20px;
    align-items: flex-start;

    ${mqUser('SP')} {
        flex-direction: column;
        align-items: center;
    }
`;

const Text = styled.div`
    font-weight: 700;
    font-size: 16px;
    line-height: 21px;
    letter-spacing: 10%;
    text-align: center;
    cursor: pointer;
    display: flex;
    align-items: center;
    margin: 0 auto;
    gap: 1px;
    :hover {
        opacity: 0.8;
    }

    img {
        width: 16px;
        height: 16px;
        margin: 0;
    }
`;

const ImgContainer = styled.div`
    width: 100%;
    max-width: 140px;
    margin: 0 auto;

    img {
        width: 100%;
        height: auto;
        max-height: 110px;
        border-radius: 8px;
        object-fit: cover;
    }

    ${mqUser('SP')} {
        margin: 0;
    }
`;

const Description = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    text-align: center;
    flex-grow: 1;

    ${mqUser('SP')} {
        align-items: flex-start;
        text-align: left;
        width: 100%;
    }
`;

const MessageContainer = styled.div`
    background: var(--white);
    border-radius: 10px;
    padding: 16px;
    font-size: 14px;
    white-space: pre-wrap;
    line-height: 1.6;
    word-break: break-word;
    position: relative;
    width: 100%;
    text-align: left;

    &::before {
        content: '';
        position: absolute;
        top: 12px;
        left: -10px;
        border: 10px solid transparent;
        border-right-color: var(--white);
    }

    ${mqUser('SP')} {
        &::before {
            display: none;
        }
    }
`;

const Buttons = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: center;
    gap: 12px;

    ${mqUser('SP')} {
        position: static;
        justify-content: flex-start;
        flex-direction: column;
        gap: 12px;
        width: 100%;

        button:nth-of-type(1) {
            order: 2;
        }
        button:nth-of-type(2) {
            order: 1;
        }
    }
`;

const SubmitButton = styled.button`
    background: var(--primary);
    color: var(--white);
    padding: 10px 20px;
    border-radius: 5px;
    cursor: pointer;
    font-size: var(--font-large);
    font-weight: 600;
    border: none;
    white-space: nowrap;
    height: fit-content;
`;

const DeclineButton = styled.button`
    background: #696969;
    color: var(--white);
    padding: 10px 20px;
    border-radius: 5px;
    cursor: pointer;
    font-size: var(--font-large);
    font-weight: 600;
    border: none;
    white-space: nowrap;
    height: fit-content;
`;

const Cards = styled.div`
    display: flex;
    flex-direction: column;
    gap: 20px;
`;

/**
 * プレミアムオファー通知モーダル
 */
export const PremiumOffer: React.FC<{
    premiumOffers: IMatch[];
    onClickDeclineButton: (match_id: number) => void;
    onClickAcceptButton: (match_id: number) => void;
    loading: boolean;
}> = ({ premiumOffers, onClickDeclineButton, onClickAcceptButton, loading }) => {
    const { user } = useContext(buyerUserContext);
    if (!user) return null;

    return (
        <Wrapper>
            <Title>
                プレミアムオファーが
                {premiumOffers.length}件届いています！
            </Title>
            <Cards>
                {premiumOffers.map((request_meeting) => (
                    <div key={request_meeting.id}>
                        <>
                            <Card>
                                <Text
                                    onClick={() => {
                                        window.open(`/buyer/agent_profile/${request_meeting.agent.id}/`, '_blank');
                                    }}
                                >
                                    <b>{request_meeting.agent.name}様</b>
                                    <img src={LinkIcon} alt={'リンクアイコン'} />
                                </Text>
                                <DescContainer>
                                    <ImgContainer>
                                        <img
                                            src={request_meeting.agent?.icon_image || NoImage}
                                            alt={request_meeting.agent.name}
                                        />
                                    </ImgContainer>
                                    <Description>
                                        {request_meeting.message_from_brokerage && (
                                            <MessageContainer>
                                                {request_meeting.message_from_brokerage}
                                            </MessageContainer>
                                        )}
                                    </Description>
                                </DescContainer>

                                <Buttons>
                                    <DeclineButton
                                        disabled={loading}
                                        onClick={() => onClickDeclineButton(request_meeting.id)}
                                    >
                                        お断り
                                    </DeclineButton>
                                    <SubmitButton
                                        onClick={() => {
                                            onClickAcceptButton(request_meeting.id);
                                        }}
                                        disabled={loading}
                                    >
                                        承認する
                                    </SubmitButton>
                                </Buttons>
                            </Card>
                        </>
                    </div>
                ))}
            </Cards>
        </Wrapper>
    );
};
